import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mic, Youtube, Headphones, Radio, ExternalLink, Tv, Wifi } from "lucide-react";

const AUDIO_LINKS = [
  { name: "Spreaker", url: "https://www.spreaker.com/podcast/10-minutes-of-talk--5933432" },
  { name: "Apple Podcasts", url: "https://podcasts.apple.com/us/podcast/10-minutes-of-talk/id552424353?uo=4" },
  { name: "Spotify", url: "https://open.spotify.com/show/3yXfSbL5ftBffkOT1hzshi" },
  { name: "iHeartRadio", url: "https://iheart.com/podcast/121731631" },
  { name: "Amazon Music", url: "https://music.amazon.com/podcasts/8c1cf2a0-025e-47c5-b176-5a73ad0b464d" },
  { name: "Deezer", url: "https://www.deezer.com/show/1000206185" },
  { name: "Castbox", url: "https://castbox.fm/channel/id5564375" },
  { name: "Podcast Addict", url: "https://podcastaddict.com/podcast/10-minutes-of-talk/4590129" },
  { name: "Podchaser", url: "https://www.podchaser.com/podcasts/10-minutes-of-talk-5933432" },
];

const PodcastSuiteZone = () => {
  return (
    <div className="space-y-8" data-testid="podcast-suite-zone">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Mic className="w-6 h-6 text-[#39FF14]" /> 10 Minutes of Talk
          </h2>
          <p className="text-neutral-400 text-sm mt-1">Quick, actionable business insights — audio & video</p>
        </div>
        <a href="/media" className="text-[#39FF14] text-sm hover:underline flex items-center gap-1">
          Full Page <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Audio + Video side by side */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Audio Podcast */}
        <Card className="bg-neutral-800 border-neutral-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Headphones className="w-5 h-5 text-[#39FF14]" />
              <h3 className="font-semibold text-white">Audio Podcast</h3>
            </div>
            <div className="rounded-xl overflow-hidden border border-neutral-700 mb-4">
              <iframe
                src="https://widget.spreaker.com/player?show_id=5933432&theme=dark&playlist=show&playlist-hierarchical=true&chapters-image=true"
                style={{ width: "100%", height: "300px" }}
                title="10 Minutes of Talk - Spreaker"
                allow="autoplay"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {AUDIO_LINKS.map(l => (
                <a key={l.name} href={l.url} target="_blank" rel="noopener noreferrer"
                  className="px-2.5 py-1 bg-neutral-700 hover:bg-neutral-600 text-neutral-300 text-[10px] rounded-full transition-colors flex items-center gap-1">
                  {l.name} <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* YouTube Podcast */}
        <Card className="bg-neutral-800 border-neutral-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Youtube className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-white">Video Podcast</h3>
            </div>
            <div className="rounded-xl overflow-hidden border border-neutral-700 mb-4">
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/videoseries?list=PLoGeha5n_c6tASgiYtlgR5Kwh2Np4kbND"
                  title="10 Minutes of Talk - YouTube"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
            <a href="https://www.youtube.com/watch?v=Np-sKbTfdco&list=PLoGeha5n_c6tASgiYtlgR5Kwh2Np4kbND"
              target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 w-full">
                <Youtube className="w-4 h-4 mr-2" /> Open Full Playlist
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>

      {/* Live Stream Section */}
      <Card className="bg-neutral-800 border-neutral-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wifi className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-white">Live Stream</h3>
            </div>
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-1.5 animate-pulse" />
              Live
            </Badge>
          </div>
          <div className="rounded-xl overflow-hidden border border-neutral-700">
            <div className="aspect-video">
              <iframe
                src="https://www.youtube.com/embed/0t_ni1yiBiU?autoplay=0"
                title="My Biz Consulting - Live Stream"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-neutral-400">Streaming on YouTube</p>
            <a href="https://youtube.com/live/0t_ni1yiBiU?feature=share" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                <Youtube className="w-4 h-4 mr-1" /> Open in YouTube
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* In Da Streets Radio */}
      <Card className="bg-gradient-to-r from-red-900/20 to-neutral-800 border-neutral-700">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <Radio className="w-6 h-6 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white">Powered by In Da Streets Radio</h3>
            <p className="text-xs text-neutral-400">Internet radio station & digital media platform</p>
          </div>
          <a href="https://indastreetsradio.com/" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              Visit <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default PodcastSuiteZone;
